import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiX, FiPlus, FiTrash2, FiCalendar, FiSearch, FiPackage, FiFileText, FiTruck, FiUser, FiShoppingCart } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { useCompras } from '../../hooks/useCompras';
import { useNotifications } from '../../components/UI/Notifications/NotificationProvider';
import { proveedoresService, productosService } from '../../services';
import { labelDepartamento, labelCategoria } from '../../utils/productLabels';
import MultiProductPicker from '../../components/UI/MultiProductPicker';
import { useProductos } from '../../hooks/useProductos';
import { rankProductMatches } from '../../utils/rankProductMatches';

const CompraForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const dropdownRef = useRef(null);

  const { crearOrden, actualizarOrden, getOrdenById } = useCompras();
  const { showError } = useNotifications();

  // Estados del formulario
  const [formData, setFormData] = useState({
    supplier_id: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    priority: 'NORMAL',
    notes: '',
    supplier_notes: '',
    shipping_cost: 0,
    discount_amount: 0,
    tax_rate: 0,
    items: []
  });

  const [proveedores, setProveedores] = useState([]);
  const { cachedProductos, searchProductos, isLoading: productosLoading, completedSearchQuery } = useProductos({ supplierId: formData.supplier_id || 'none' });
  const productos = cachedProductos.map(product => ({ ...product, name: product.nombreProducto, code: product.codigo, product_type: product.departamento, product_types: product.departamentos, cost_price: product.costo, current_stock: product.stock }));
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [itemQuantity, setItemQuantity] = useState(1);

  // Cargar datos iniciales
  useEffect(() => {
    loadProveedores();

    if (isEditing) {
      loadOrdenData();
    }
  }, [id, isEditing]);

  const getDepartments = product => (product.product_types?.length ? product.product_types : [product.product_type || product.departamento]).map(value => String(value || '').toUpperCase());
  // En órdenes de compra el único criterio de elegibilidad es el proveedor asociado.
  // El departamento se muestra como información, pero nunca excluye productos.
  // Búsqueda de productos: mismo buscador API y orden por nombre que preórdenes.
  useEffect(() => {
    if (formData.supplier_id && productSearch.trim()) searchProductos(productSearch);
  }, [productSearch]);

  // Búsqueda de productos
  useEffect(() => {
    if (formData.supplier_id && productSearch.trim().length > 0) {
      setSearchResults(rankProductMatches(productos.map(product => ({ ...product, nombreProducto: product.nombreProducto || product.name })), productSearch));
      setShowDropdown(true);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
    }
  }, [productSearch, cachedProductos, formData.supplier_id]);

  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadProveedores = async () => {
    try {
      const result = await proveedoresService.getProveedores({ limit: 1000, is_active: true });
      if (result.success) {
        setProveedores(
          result.data
            .filter(p => p.is_active === true && p.status === 'ACTIVE')
            .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es', { sensitivity: 'base' }))
        );
      }
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const loadOrdenData = async () => {
    setIsLoading(true);
    try {
      const result = await getOrdenById(id);
      if (result.success) {
        setFormData(result.data);
      } else {
        showError('Error al cargar la orden de compra.', { title: 'Error de carga' });
      }
    } catch (error) {
      showError('Error de conexión al cargar la orden.', { title: 'Error de conexión' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'supplier_id') {
      setProductSearch('');
      setSearchResults([]);
      setShowDropdown(false);
      setSelectedProduct(null);
    }
    setFormData(prev => ({
      ...prev,
      [field]: typeof value === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setProductSearch(product.name || product.nombreProducto);
    setSearchResults([]);
    setShowDropdown(false);
  };

  const addItemToOrden = () => {
    const qty = parseFloat(itemQuantity);
    if (!selectedProduct || !qty || qty <= 0) {
      showError('Seleccione un producto y especifique una cantidad válida.', { title: 'Falta completar: Producto' });
      return;
    }

    const newItem = {
      product_id: selectedProduct.id,
      quantity: qty,
      unit_cost: selectedProduct.cost_price || selectedProduct.costoUnitario || 0,
      description: selectedProduct.name || selectedProduct.nombreProducto,
      notes: '',
      expected_delivery_date: ''
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    // Limpiar formulario de producto
    setSelectedProduct(null);
    setProductSearch('');
    setItemQuantity(1);
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index
          ? {
              ...item,
              // Se permite dejar el campo vacío mientras se escribe (antes
              // parseFloat(x)||0 forzaba un 0 imposible de borrar); el valor
              // se normaliza a número al calcular totales y al guardar.
              [field]: field === 'quantity' || field === 'unit_cost'
                ? (value === '' ? '' : parseFloat(value) || 0)
                : value
            }
          : item
      )
    }));
  };

  // Convierte a número tolerando el estado intermedio '' (campo vacío)
  const toNum = (v) => parseFloat(v) || 0;

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) =>
      sum + (toNum(item.quantity) * toNum(item.unit_cost)), 0
    );
    const taxAmount = subtotal * (toNum(formData.tax_rate) / 100);
    const total = subtotal + taxAmount + toNum(formData.shipping_cost) - toNum(formData.discount_amount);

    return { subtotal, taxAmount, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();


    if (!formData.supplier_id) {
      showError('Seleccione un proveedor.', { title: 'Falta completar: Proveedor' });
      return;
    }

    if (!formData.order_date) {
      showError('Ingrese la fecha de la orden.', { title: 'Falta completar: Fecha de Orden' });
      return;
    }

    if (formData.items.length === 0) {
      showError('Agregue al menos un producto.', { title: 'Falta completar: Productos' });
      return;
    }

    setIsSaving(true);

    try {
      const totals = calculateTotals();
      const orderData = {
        ...formData,
        // La fecha de entrega es opcional; la API acepta null, no una cadena vacía.
        expected_delivery_date: formData.expected_delivery_date || null,
        // Normalizar posibles campos vacíos ('') a número antes de enviar
        shipping_cost: toNum(formData.shipping_cost),
        discount_amount: toNum(formData.discount_amount),
        tax_rate: toNum(formData.tax_rate),
        items: formData.items.map(item => ({
          ...item,
          quantity: toNum(item.quantity),
          unit_cost: toNum(item.unit_cost)
        })),
        subtotal: totals.subtotal,
        tax_amount: totals.taxAmount,
        total_amount: totals.total
      };

      const result = isEditing
        ? await actualizarOrden(id, orderData)
        : await crearOrden(orderData);

      if (result.success) {
        navigate('/system/compras');
      } else {
        const detail = result.error || result.message || 'No se pudo guardar la orden de compra';
        const detailText = typeof detail === 'string' ? detail : Array.isArray(detail)
          ? detail.map(item => `${Array.isArray(item.loc) ? item.loc.filter(part => part !== 'body').join(' → ') : 'Campo'}: ${item.msg || 'valor inválido'}`).join('; ')
          : (detail.detail || detail.message || JSON.stringify(detail));
        showError(detailText, { title: isEditing ? 'No se pudo actualizar la orden' : 'No se pudo crear la orden' });
      }
    } catch (error) {
      showError('Error al guardar la orden de compra.', { title: 'Error de guardado' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/system/compras');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getProductName = (productId) => {
    const product = productos.find(p => p.id === parseInt(productId));
    return product ? (product.name || product.nombreProducto) : '';
  };

  const totals = calculateTotals();

  if (isLoading) {
    return <LoadingGrid message="Cargando orden de compra..." />;
  }

  return (
    <div className="factura-form-page compra-form-page">
      {/* Header Premium */}
      <div className="page-header">
        <button className="btn btn--ghost header-back" onClick={handleCancel}>
          <FiArrowLeft size={20} />
        </button>

        <h1 className="page-title page-title--centered">
          {isEditing ? 'Editar Orden de Compra' : 'Nueva Orden de Compra'}
        </h1>

        <div className="header-actions">
          <button className="btn btn--outline" onClick={handleCancel}>
            <FiX size={16} />
            Cancelar
          </button>
          <button
            className="btn btn--primary"
            onClick={handleSubmit}
            type="button"
            disabled={isSaving}
          >
            <FiSave size={16} />
            {isSaving ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear Orden')}
          </button>
        </div>
      </div>

      {/* Formulario */}
      <form onSubmit={handleSubmit} noValidate className="factura-form">
        {/* Card 1: Información Principal - Diseño Premium */}
        <div className="invoice-header-card">
          <div className="invoice-header-card__left">
            <div className="invoice-icon-circle">
              <FiFileText size={32} />
            </div>
            <div className="invoice-header-info">
              <h3 className="invoice-header-title">
                {isEditing ? 'Orden #' + id : 'Nueva Orden de Compra'}
              </h3>
              <p className="invoice-header-subtitle">
                Complete los detalles de la orden
              </p>
            </div>
          </div>

          <div className="invoice-header-card__grid">
            {/* Proveedor */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiUser size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Proveedor *</label>
                <select
                  className="invoice-field-input"
                  value={formData.supplier_id}
                  onChange={(e) => handleInputChange('supplier_id', e.target.value)}
                >
                  <option value="">Seleccionar proveedor...</option>
                  {proveedores.map(proveedor => (
                    <option key={proveedor.id} value={proveedor.id}>
                      {proveedor.name || proveedor.nombreEmpresa}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Prioridad */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiFileText size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Prioridad</label>
                <select
                  className="invoice-field-input"
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                >
                  <option value="LOW">Baja</option>
                  <option value="NORMAL">Normal</option>
                  <option value="HIGH">Alta</option>
                  <option value="URGENT">Urgente</option>
                </select>
              </div>
            </div>

            {/* Fecha de Orden */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiCalendar size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Fecha de Orden *</label>
                <input
                  type="date"
                  className="invoice-field-input"
                  value={formData.order_date}
                  onChange={(e) => handleInputChange('order_date', e.target.value)}
                />
              </div>
            </div>

            {/* Fecha de Entrega Esperada */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiTruck size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Fecha de Entrega</label>
                <input
                  type="date"
                  className="invoice-field-input"
                  value={formData.expected_delivery_date}
                  onChange={(e) => handleInputChange('expected_delivery_date', e.target.value)}
                />
              </div>
            </div>

            {/* Notas Internas - Ocupa 2 columnas */}
            <div className="invoice-field invoice-field--full">
              <div className="invoice-field-icon">
                <FiFileText size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Notas Internas</label>
                <textarea
                  className="invoice-field-input invoice-field-textarea"
                  placeholder="Notas para uso interno..."
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows="2"
                />
              </div>
            </div>

            {/* Notas para Proveedor - Ocupa 2 columnas */}
            <div className="invoice-field invoice-field--full">
              <div className="invoice-field-icon">
                <FiFileText size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Notas para el Proveedor</label>
                <textarea
                  className="invoice-field-input invoice-field-textarea"
                  placeholder="Notas que se enviarán al proveedor..."
                  value={formData.supplier_notes}
                  onChange={(e) => handleInputChange('supplier_notes', e.target.value)}
                  rows="2"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Buscador de Productos - Diseño Moderno */}
        <div className="product-search-card">
          <div className="product-search-header">
            <div className="search-header-icon">
              <FiPackage size={24} />
            </div>
            <div className="search-header-content">
              <h3 className="search-header-title">Agregar Productos</h3>
              <p className="search-header-subtitle">Busque y agregue productos a la orden</p>
            </div>
          </div>

          <div className="product-search-body">
            {/* Buscador Principal */}
            <div className="search-combo-wrapper" ref={dropdownRef} onKeyDownCapture={e => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); setShowDropdown(false); e.target.blur?.(); } }}>
              <div className="search-combo-icon-left">
                <FiSearch size={20} />
              </div>
              <input
                type="text"
                className="search-combo-input"
                placeholder={formData.supplier_id ? "Buscar productos de este proveedor..." : "Seleccione primero un proveedor"}
                disabled={!formData.supplier_id}
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setShowDropdown(true);
                  }
                }}
              />

              {productosLoading && <div className="search-combo-loading"><div className="spinner-sm"></div></div>}
              {/* Selector múltiple igual que preórdenes */}
              {showDropdown && searchResults.length > 0 && (
                <div className="product-dropdown">
                  <MultiProductPicker products={searchResults} renderMeta={product => <>
                    <span className="meta-code">{getDepartments(product).map(labelDepartamento).join(', ')}</span>
                    {product.categoria && <span className="meta-separator">·</span>}
                    {product.categoria && <span>{labelCategoria(product.categoria)}</span>}
                    <span className="meta-separator">·</span>
                    <span className="meta-stock">Stock: {product.stock ?? 0}</span>
                  </>} renderPrice={product => formatCurrency(product.costo)} onAdd={entries => {
                    setFormData(prev => ({ ...prev, items: entries.reduce((items, { product, quantity }) => {
                      const index = items.findIndex(item => item.product_id === product.id);
                      if (index < 0) return quantity > 0 ? [...items, { product_id: product.id, quantity, unit_cost: product.costo || 0, description: product.nombreProducto, notes: '', expected_delivery_date: '' }] : items;
                      const next = [...items];
                      const updated = Number(next[index].quantity) + quantity;
                      if (updated <= 0) return next.filter((_, i) => i !== index);
                      next[index] = { ...next[index], quantity: updated };
                      return next;
                    }, prev.items) }));
                                  }} />
                </div>
              )}
              {showDropdown && !productosLoading && completedSearchQuery === productSearch.trim() && searchResults.length === 0 && (
                <div className="product-dropdown"><div className="product-dropdown-item">No se encontraron productos asociados a este proveedor.</div></div>
              )}
            </div>

            {/* Vista previa del producto seleccionado */}
            {selectedProduct && (
              <div className="selected-product-card">
                <div className="selected-product-info">
                  <div className="selected-product-badge">
                    <FiPackage size={16} />
                  </div>
                  <div className="selected-product-details">
                    <h4 className="selected-product-name">
                      {selectedProduct.name || selectedProduct.nombreProducto}
                    </h4>
                    <div className="selected-product-meta">
                      <span className="meta-item">Código: {selectedProduct.code || selectedProduct.codigo}</span>
                      <span className="meta-separator">•</span>
                      <span className="meta-item">
                        Costo: {formatCurrency(selectedProduct.cost_price || selectedProduct.costoUnitario || 0)}
                      </span>
                      <span className="meta-separator">•</span>
                      <span className="meta-item">Stock: {selectedProduct.stock || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="selected-product-controls">
                  <div className="control-group">
                    <label className="control-label">Cantidad</label>
                    <input
                      type="number"
                      value={itemQuantity}
                      onChange={(e) => setItemQuantity(e.target.value)}
                      min="0.1"
                      step="0.1"
                      className="control-input"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn--primary btn--add-product"
                    onClick={addItemToOrden}
                  >
                    <FiPlus size={18} />
                    Agregar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Card 3: Tabla de Productos - Diseño Profesional */}
        {formData.items.length > 0 ? (
          <div className="invoice-table-card">
            <div className="invoice-table-header">
              <div className="table-header-icon">
                <FiShoppingCart size={24} />
              </div>
              <div className="table-header-content">
                <h3 className="table-header-title">Productos en la Orden</h3>
                <p className="table-header-subtitle">{formData.items.length} producto(s) agregado(s)</p>
              </div>
            </div>

            <div className="invoice-table-wrapper">
              <table className="invoice-table">
                <thead className="invoice-table-head">
                  <tr>
                    <th className="th-product">Producto</th>
                    <th className="th-qty">Cantidad</th>
                    <th className="th-price">Costo Unit.</th>
                    <th className="th-total">Total</th>
                    <th className="th-actions"></th>
                  </tr>
                </thead>
                <tbody className="invoice-table-body">
                  {formData.items.map((item, index) => (
                    <tr key={index} className="invoice-table-row">
                      <td className="td-product">
                        <div className="product-cell">
                          <div className="product-cell-icon">
                            <FiPackage size={16} />
                          </div>
                          <div className="product-cell-info">
                            <div className="product-cell-name">
                              {item.description || getProductName(item.product_id)}
                            </div>
                            <div className="product-cell-code">
                              ID: {item.product_id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="td-qty">
                        <input
                          type="number"
                          className="qty-input"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          min="0.01"
                          step="0.01"
                        />
                      </td>
                      <td className="td-price">
                        <input
                          type="number"
                          className="price-input"
                          value={item.unit_cost}
                          onChange={(e) => updateItem(index, 'unit_cost', e.target.value)}
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td className="td-total">
                        <span className="total-value">
                          {formatCurrency(item.quantity * item.unit_cost)}
                        </span>
                      </td>
                      <td className="td-actions">
                        <button
                          type="button"
                          className="btn-icon-delete"
                          onClick={() => removeItem(index)}
                          title="Eliminar producto"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totales y Costos Adicionales */}
            <div className="invoice-totals-section">
              {/* Costos adicionales */}
              <div className="additional-costs">
                <div className="cost-row">
                  <label>Costo de Envío</label>
                  <input
                    type="number"
                    className="cost-input"
                    value={formData.shipping_cost}
                    onChange={(e) => handleInputChange('shipping_cost', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="cost-row">
                  <label>Descuento</label>
                  <input
                    type="number"
                    className="cost-input"
                    value={formData.discount_amount}
                    onChange={(e) => handleInputChange('discount_amount', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="cost-row">
                  <label>Impuesto (%)</label>
                  <input
                    type="number"
                    className="cost-input"
                    value={formData.tax_rate}
                    onChange={(e) => handleInputChange('tax_rate', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Resumen de totales */}
              <div className="totals-container">
                <div className="totals-row">
                  <span className="totals-label">Subtotal</span>
                  <span className="totals-value">{formatCurrency(totals.subtotal)}</span>
                </div>
                <div className="totals-row">
                  <span className="totals-label">Impuestos</span>
                  <span className="totals-value">{formatCurrency(totals.taxAmount)}</span>
                </div>
                <div className="totals-row">
                  <span className="totals-label">Envío</span>
                  <span className="totals-value">{formatCurrency(formData.shipping_cost)}</span>
                </div>
                {formData.discount_amount > 0 && (
                  <div className="totals-row totals-row--discount">
                    <span className="totals-label">Descuento</span>
                    <span className="totals-value">-{formatCurrency(formData.discount_amount)}</span>
                  </div>
                )}
                <div className="totals-divider"></div>
                <div className="totals-row totals-row--final">
                  <span className="totals-label">Total</span>
                  <span className="totals-value">{formatCurrency(totals.total)}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="invoice-empty-state">
            <div className="empty-state-icon">
              <FiShoppingCart size={64} />
            </div>
            <h3 className="empty-state-title">No hay productos agregados</h3>
            <p className="empty-state-message">
              Use el buscador de productos para encontrar y agregar ítems a la orden
            </p>
          </div>
        )}
        {/* Accion inferior con el mismo guardado que el encabezado. */}
        <div className="form-actions bottom-form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '24px 0', flexWrap: 'wrap' }}>
          <button type="submit" className="btn btn--primary" disabled={isSaving}>
            <FiSave /> {isSaving ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear Orden')}
          </button>
        </div>
      </form>

    </div>
  );
};

export default CompraForm;
